import { useEffect, useState } from "react";
import api, { fmtTRY, fmtDate } from "../lib/api";
import { toast } from "sonner";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Switch } from "../components/ui/switch";
import MediaUploader from "../components/common/MediaUploader";
import { Thumb } from "../components/common/MediaUploader";
import { Plus, Pencil, Trash2, DollarSign, Tractor as TractorIcon, ShieldAlert, Eye } from "lucide-react";
import DatePicker from "../components/common/DatePicker";

const empty = {
  brand: "", model: "", year: new Date().getFullYear(),
  condition: "sifir", engine_hp: "", hours: "", drive: "4x4", cabin: "Kabinli",
  color: "", chassis_no: "", plate: "", purchase_price: 0, sale_price: 0,
  status: "stokta", description: "", media: [],
  insurance_expiry: "", inspection_expiry: "", previous_owner: "",
};

const emptySale = {
  buyer_name: "", buyer_id: "", buyer_phone: "", buyer_tax_no: "", buyer_address: "",
  sale_price: 0, payment_method: "nakit", payment_terms: "", down_payment: 0, installments: 0,
  plate: "", trade_in: false, trade_in_desc: "", trade_in_value: 0,
  contract_media: [], notes: "",
};

const daysLeft = (iso) => {
  if (!iso) return null;
  const d = new Date(iso); const now = new Date();
  return Math.ceil((d - now) / (1000 * 60 * 60 * 24));
};

export default function TractorsPage() {
  const [items, setItems] = useState([]);
  const [tab, setTab] = useState("all");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);
  const [saleFor, setSaleFor] = useState(null);
  const [sale, setSale] = useState(emptySale);
  const [customers, setCustomers] = useState([]);
  const [details, setDetails] = useState(null);

  const load = () => api.get("/tractors").then((r) => setItems(r.data));
  useEffect(() => { load(); api.get("/customers").then((r) => setCustomers(r.data)); }, []);

  const filtered = items.filter((t) =>
    tab === "all" ? true :
    tab === "sifir" ? t.condition === "sifir" :
    tab === "ikinci" ? t.condition === "ikinci_el" :
    tab === "sold" ? t.status === "satildi" : true
  );

  const submit = async () => {
    if (!form.brand || !form.model) return toast.error("Marka ve model zorunlu");
    const payload = {
      ...form,
      year: form.year ? Number(form.year) : null,
      engine_hp: form.engine_hp ? Number(form.engine_hp) : null,
      hours: form.hours ? Number(form.hours) : null,
      purchase_price: Number(form.purchase_price) || 0,
      sale_price: Number(form.sale_price) || 0,
      insurance_expiry: form.insurance_expiry || null,
      inspection_expiry: form.inspection_expiry || null,
    };
    try {
      if (editing) await api.put(`/tractors/${editing}`, payload);
      else await api.post("/tractors", payload);
      toast.success("Kaydedildi");
      setOpen(false); setForm(empty); setEditing(null); load();
    } catch { toast.error("Kayıt başarısız"); }
  };

  const openSale = (t) => {
    setSaleFor(t);
    setSale({ ...emptySale, sale_price: t.sale_price, plate: t.plate || "" });
  };

  const doSell = async () => {
    if (!sale.buyer_name || !sale.sale_price) return toast.error("Alıcı adı ve tutar gerekli");
    try {
      await api.post(`/tractors/${saleFor.id}/sell`, {
        ...sale,
        sale_price: Number(sale.sale_price),
        down_payment: Number(sale.down_payment) || 0,
        installments: Number(sale.installments) || 0,
        trade_in_value: Number(sale.trade_in_value) || 0,
      });
      toast.success("Satış kaydedildi");
      setSaleFor(null); load();
    } catch { toast.error("Satış kaydedilemedi"); }
  };

  const edit = (t) => { setEditing(t.id); setForm({ ...empty, ...t, media: t.media || [] }); setOpen(true); };
  const remove = async (id) => { if (window.confirm("Silinsin mi?")) { await api.delete(`/tractors/${id}`); toast.success("Silindi"); load(); } };

  return (
    <div className="space-y-5" data-testid="tractors-page">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[11px] uppercase tracking-[0.22em] font-bold text-[#1B4D3E]">Envanter</div>
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight text-slate-900">Traktör Stoğu</h1>
        </div>
        <Button className="bg-[#1B4D3E] hover:bg-[#163e32] text-white gap-2" onClick={() => { setEditing(null); setForm(empty); setOpen(true); }} data-testid="add-tractor-btn">
          <Plus size={16} /> Yeni Traktör
        </Button>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="all" data-testid="tab-all">Tümü</TabsTrigger>
          <TabsTrigger value="sifir" data-testid="tab-sifir">Sıfır</TabsTrigger>
          <TabsTrigger value="ikinci" data-testid="tab-ikinci">2. El</TabsTrigger>
          <TabsTrigger value="sold" data-testid="tab-sold">Satılan</TabsTrigger>
        </TabsList>
      </Tabs>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
        {filtered.map((t) => {
          const insDL = daysLeft(t.insurance_expiry);
          const inspDL = daysLeft(t.inspection_expiry);
          return (
            <Card key={t.id} className="border-slate-200 tp-fade overflow-hidden" data-testid={`tractor-card-${t.id}`}>
              <div className="aspect-video bg-slate-100 relative">
                {t.media?.[0] ? <Thumb id={t.media[0]} /> : <div className="w-full h-full grid place-items-center text-slate-400"><TractorIcon size={40} /></div>}
                <div className="absolute top-2 left-2 flex gap-2 flex-wrap">
                  <Badge className={t.condition === "sifir" ? "bg-[#1B4D3E] text-white" : "bg-slate-900 text-white"}>
                    {t.condition === "sifir" ? "SIFIR" : "2. EL"}
                  </Badge>
                  {t.status === "satildi" && <Badge className="bg-rose-600 text-white">SATILDI</Badge>}
                  {t.status === "rezerve" && <Badge className="bg-amber-500 text-slate-900">REZERVE</Badge>}
                </div>
                {t.plate && <Badge className="absolute top-2 right-2 bg-white/95 text-slate-900 font-mono border">{t.plate}</Badge>}
              </div>
              <CardContent className="p-4 space-y-2">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="font-bold text-slate-900">{t.brand} {t.model}</div>
                    <div className="text-xs text-slate-500">{t.year || "—"} · {t.engine_hp ? `${t.engine_hp} HP` : ""} {t.drive ? `· ${t.drive}` : ""}</div>
                  </div>
                  <div className="text-right">
                    <div className="font-extrabold text-[#1B4D3E]">{fmtTRY(t.sale_price)}</div>
                    <div className="text-[10px] text-slate-400">{fmtDate(t.created_at)}</div>
                  </div>
                </div>
                {t.hours != null && <div className="text-xs text-slate-500">{t.hours} saat çalışma</div>}

                {t.condition === "ikinci_el" && (insDL != null || inspDL != null) && (
                  <div className="flex flex-wrap gap-1">
                    {insDL != null && (
                      <Badge variant="outline" className={insDL < 30 ? "border-rose-300 text-rose-700" : "border-slate-300 text-slate-600"}>
                        <ShieldAlert size={11} className="mr-1" /> Sigorta: {insDL >= 0 ? `${insDL} gün` : "GEÇTİ"}
                      </Badge>
                    )}
                    {inspDL != null && (
                      <Badge variant="outline" className={inspDL < 30 ? "border-rose-300 text-rose-700" : "border-slate-300 text-slate-600"}>
                        Muayene: {inspDL >= 0 ? `${inspDL} gün` : "GEÇTİ"}
                      </Badge>
                    )}
                  </div>
                )}

                {t.status === "satildi" && t.buyer_name && (
                  <div className="text-xs bg-emerald-50 border border-emerald-200 rounded p-2">
                    <div className="text-[10px] uppercase font-bold text-emerald-700">Alıcı</div>
                    <div className="font-semibold text-slate-800">{t.buyer_name}</div>
                  </div>
                )}

                <div className="flex gap-1 pt-1">
                  {t.status !== "satildi" && (
                    <Button size="sm" className="bg-[#D97706] hover:bg-[#B45309] text-white gap-1 flex-1" onClick={() => openSale(t)} data-testid={`sell-tractor-${t.id}`}>
                      <DollarSign size={14} /> Sat
                    </Button>
                  )}
                  <Button size="sm" variant="outline" onClick={() => setDetails(t)} data-testid={`view-tractor-${t.id}`}><Eye size={14} /></Button>
                  <Button size="sm" variant="outline" onClick={() => edit(t)} data-testid={`edit-tractor-${t.id}`}><Pencil size={14} /></Button>
                  <Button size="sm" variant="outline" onClick={() => remove(t.id)} data-testid={`delete-tractor-${t.id}`}><Trash2 size={14} className="text-rose-600" /></Button>
                </div>
              </CardContent>
            </Card>
          );
        })}
        {filtered.length === 0 && <div className="col-span-full text-center py-10 text-slate-500">Kayıt yok. Yeni traktör ekleyin.</div>}
      </div>

      {/* Add/Edit */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? "Traktör Düzenle" : "Yeni Traktör"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div><Label>Marka *</Label><Input value={form.brand} onChange={(e) => setForm({ ...form, brand: e.target.value })} data-testid="tr-brand" /></div>
            <div><Label>Model *</Label><Input value={form.model} onChange={(e) => setForm({ ...form, model: e.target.value })} data-testid="tr-model" /></div>
            <div><Label>Yıl</Label><Input type="number" value={form.year} onChange={(e) => setForm({ ...form, year: e.target.value })} data-testid="tr-year" /></div>
            <div><Label>Durum</Label>
              <Select value={form.condition} onValueChange={(v) => setForm({ ...form, condition: v })}>
                <SelectTrigger data-testid="tr-condition"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="sifir">Sıfır</SelectItem>
                  <SelectItem value="ikinci_el">İkinci El</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label>Motor Gücü (HP)</Label><Input type="number" value={form.engine_hp} onChange={(e) => setForm({ ...form, engine_hp: e.target.value })} data-testid="tr-hp" /></div>
            <div><Label>Çalışma Saati</Label><Input type="number" value={form.hours} onChange={(e) => setForm({ ...form, hours: e.target.value })} data-testid="tr-hours" /></div>
            <div><Label>Çekiş</Label>
              <Select value={form.drive} onValueChange={(v) => setForm({ ...form, drive: v })}>
                <SelectTrigger data-testid="tr-drive"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="4x4">4x4</SelectItem><SelectItem value="4x2">4x2</SelectItem></SelectContent>
              </Select>
            </div>
            <div><Label>Kabin</Label>
              <Select value={form.cabin} onValueChange={(v) => setForm({ ...form, cabin: v })}>
                <SelectTrigger data-testid="tr-cabin"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="Kabinli">Kabinli</SelectItem><SelectItem value="Kabinsiz">Kabinsiz</SelectItem><SelectItem value="Rops">Rops</SelectItem></SelectContent>
              </Select>
            </div>
            <div><Label>Renk</Label><Input value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })} data-testid="tr-color" /></div>
            <div><Label>Plaka</Label><Input value={form.plate} onChange={(e) => setForm({ ...form, plate: e.target.value })} placeholder="34 ABC 123" data-testid="tr-plate" /></div>
            <div><Label>Şasi No</Label><Input value={form.chassis_no} onChange={(e) => setForm({ ...form, chassis_no: e.target.value })} data-testid="tr-chassis" /></div>
            <div><Label>Alış Fiyatı (₺)</Label><Input type="number" value={form.purchase_price} onChange={(e) => setForm({ ...form, purchase_price: e.target.value })} data-testid="tr-purchase" /></div>
            <div><Label>Satış Fiyatı (₺)</Label><Input type="number" value={form.sale_price} onChange={(e) => setForm({ ...form, sale_price: e.target.value })} data-testid="tr-price" /></div>

            {form.condition === "ikinci_el" && (
              <>
                <div className="sm:col-span-2 pt-2 border-t border-slate-200">
                  <div className="text-[11px] uppercase tracking-widest font-bold text-slate-500 mb-1">İkinci El Ek Bilgiler</div>
                </div>
                <div><Label>Sigorta Bitiş</Label><DatePicker clearable value={form.insurance_expiry} onChange={(v) => setForm({ ...form, insurance_expiry: v })} testid="tr-ins" /></div>
                <div><Label>Muayene Bitiş</Label><DatePicker clearable value={form.inspection_expiry} onChange={(v) => setForm({ ...form, inspection_expiry: v })} testid="tr-insp" /></div>
                <div className="sm:col-span-2"><Label>Önceki Sahip</Label><Input value={form.previous_owner} onChange={(e) => setForm({ ...form, previous_owner: e.target.value })} data-testid="tr-prev-owner" /></div>
              </>
            )}

            <div className="sm:col-span-2"><Label>Açıklama</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="tr-desc" /></div>
            <div className="sm:col-span-2">
              <Label>Fotoğraf / Video</Label>
              <MediaUploader value={form.media} onChange={(m) => setForm({ ...form, media: m })} testid="tr-media" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>İptal</Button>
            <Button className="bg-[#1B4D3E] hover:bg-[#163e32]" onClick={submit} data-testid="save-tractor-btn">Kaydet</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Sale */}
      <Dialog open={!!saleFor} onOpenChange={(v) => !v && setSaleFor(null)}>
        <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Traktör Satışı</DialogTitle></DialogHeader>
          {saleFor && (
            <div className="space-y-4">
              <div className="text-sm text-slate-600 bg-slate-50 border border-slate-200 rounded-lg p-3">
                <div className="font-bold text-slate-900">{saleFor.brand} {saleFor.model}</div>
                <div className="text-xs">{saleFor.year} · {saleFor.engine_hp} HP · <Badge className={saleFor.condition === "sifir" ? "bg-[#1B4D3E] text-white ml-1" : "bg-slate-900 text-white ml-1"}>{saleFor.condition === "sifir" ? "SIFIR" : "2. EL"}</Badge></div>
              </div>

              {/* Alıcı bilgileri */}
              <div>
                <div className="text-[11px] uppercase tracking-widest font-bold text-slate-500 mb-2">Alıcı Bilgileri</div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="sm:col-span-2">
                    <Label>Müşteri</Label>
                    <Select value={sale.buyer_id || ""} onValueChange={(v) => {
                      const c = customers.find((x) => x.id === v);
                      setSale({ ...sale, buyer_id: v, buyer_name: c?.name || "", buyer_phone: c?.phone || "", buyer_tax_no: c?.tax_no || "", buyer_address: c?.address || "" });
                    }}>
                      <SelectTrigger data-testid="sale-customer"><SelectValue placeholder="Müşteri seç (opsiyonel)" /></SelectTrigger>
                      <SelectContent>{customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div><Label>Ad Soyad / Firma *</Label><Input value={sale.buyer_name} onChange={(e) => setSale({ ...sale, buyer_name: e.target.value })} data-testid="sale-buyer" /></div>
                  <div><Label>Telefon</Label><Input value={sale.buyer_phone} onChange={(e) => setSale({ ...sale, buyer_phone: e.target.value })} data-testid="sale-buyer-phone" /></div>
                  <div><Label>Vergi/TC No</Label><Input value={sale.buyer_tax_no} onChange={(e) => setSale({ ...sale, buyer_tax_no: e.target.value })} data-testid="sale-buyer-tax" /></div>
                  <div><Label>Plaka</Label><Input value={sale.plate} onChange={(e) => setSale({ ...sale, plate: e.target.value })} placeholder="Aracın alacağı plaka" data-testid="sale-plate" /></div>
                  <div className="sm:col-span-2"><Label>Adres</Label><Textarea value={sale.buyer_address} onChange={(e) => setSale({ ...sale, buyer_address: e.target.value })} data-testid="sale-buyer-address" /></div>
                </div>
              </div>

              {/* Ödeme koşulları */}
              <div>
                <div className="text-[11px] uppercase tracking-widest font-bold text-slate-500 mb-2">Ödeme Koşulları</div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div><Label>Satış Bedeli (₺) *</Label><Input type="number" value={sale.sale_price} onChange={(e) => setSale({ ...sale, sale_price: e.target.value })} data-testid="sale-price" /></div>
                  <div><Label>Ödeme Şekli</Label>
                    <Select value={sale.payment_method} onValueChange={(v) => setSale({ ...sale, payment_method: v })}>
                      <SelectTrigger data-testid="sale-method"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="nakit">Nakit</SelectItem>
                        <SelectItem value="havale">Banka/Havale</SelectItem>
                        <SelectItem value="cek">Çek</SelectItem>
                        <SelectItem value="kredi_karti">Kredi Kartı</SelectItem>
                        <SelectItem value="kredi">Banka Kredisi</SelectItem>
                        <SelectItem value="senet">Senet</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div><Label>Peşinat (₺)</Label><Input type="number" value={sale.down_payment} onChange={(e) => setSale({ ...sale, down_payment: e.target.value })} data-testid="sale-down" /></div>
                  <div><Label>Taksit Sayısı</Label><Input type="number" value={sale.installments} onChange={(e) => setSale({ ...sale, installments: e.target.value })} data-testid="sale-installments" /></div>
                  <div className="sm:col-span-2"><Label>Ödeme Koşulları / Not</Label><Textarea value={sale.payment_terms} onChange={(e) => setSale({ ...sale, payment_terms: e.target.value })} placeholder="Örn: %50 peşin + 6 taksit senet" data-testid="sale-terms" /></div>
                </div>
              </div>

              {/* Takas */}
              {saleFor.condition === "ikinci_el" && (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-[11px] uppercase tracking-widest font-bold text-slate-500">Takas</div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-slate-600">Takasla mı?</span>
                      <Switch checked={sale.trade_in} onCheckedChange={(v) => setSale({ ...sale, trade_in: v })} data-testid="sale-trade-toggle" />
                    </div>
                  </div>
                  {sale.trade_in && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div className="sm:col-span-2"><Label>Takas Edilen (marka/model/yıl)</Label><Input value={sale.trade_in_desc} onChange={(e) => setSale({ ...sale, trade_in_desc: e.target.value })} placeholder="Örn: 2015 New Holland T480, 4500 saat" data-testid="sale-trade-desc" /></div>
                      <div><Label>Takas Değeri (₺)</Label><Input type="number" value={sale.trade_in_value} onChange={(e) => setSale({ ...sale, trade_in_value: e.target.value })} data-testid="sale-trade-value" /></div>
                    </div>
                  )}
                </div>
              )}

              {/* Sözleşme yükleme */}
              <div>
                <Label>Satış Sözleşmesi (fotoğraf/PDF taraması)</Label>
                <MediaUploader value={sale.contract_media} onChange={(m) => setSale({ ...sale, contract_media: m })} testid="sale-contract" />
              </div>

              <div>
                <Label>Ek Not</Label>
                <Textarea value={sale.notes} onChange={(e) => setSale({ ...sale, notes: e.target.value })} data-testid="sale-notes" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setSaleFor(null)}>İptal</Button>
            <Button className="bg-[#D97706] hover:bg-[#B45309]" onClick={doSell} data-testid="confirm-sale-btn">Satışı Onayla</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Details */}
      <Dialog open={!!details} onOpenChange={(v) => !v && setDetails(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          {details && (
            <>
              <DialogHeader><DialogTitle>{details.brand} {details.model}</DialogTitle></DialogHeader>
              <div className="space-y-3">
                {details.media?.length > 0 && (
                  <div className="grid grid-cols-3 gap-2">
                    {details.media.map((m) => <Thumb key={m} id={m} />)}
                  </div>
                )}
                <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                  <div><span className="text-slate-500">Yıl:</span> {details.year || "—"}</div>
                  <div><span className="text-slate-500">Motor:</span> {details.engine_hp ? `${details.engine_hp} HP` : "—"}</div>
                  <div><span className="text-slate-500">Çekiş:</span> {details.drive || "—"}</div>
                  <div><span className="text-slate-500">Kabin:</span> {details.cabin || "—"}</div>
                  <div><span className="text-slate-500">Renk:</span> {details.color || "—"}</div>
                  <div><span className="text-slate-500">Saat:</span> {details.hours ?? "—"}</div>
                  <div><span className="text-slate-500">Plaka:</span> {details.plate || "—"}</div>
                  <div><span className="text-slate-500">Şasi:</span> <span className="font-mono">{details.chassis_no || "—"}</span></div>
                  {details.condition === "ikinci_el" && (
                    <>
                      <div><span className="text-slate-500">Sigorta:</span> {details.insurance_expiry ? fmtDate(details.insurance_expiry) : "—"}</div>
                      <div><span className="text-slate-500">Muayene:</span> {details.inspection_expiry ? fmtDate(details.inspection_expiry) : "—"}</div>
                      <div className="col-span-2"><span className="text-slate-500">Önceki Sahip:</span> {details.previous_owner || "—"}</div>
                    </>
                  )}
                </div>
                <div className="border-t pt-3 flex items-center justify-between">
                  <div>
                    <div className="text-xs uppercase tracking-widest font-bold text-slate-500">Fiyat</div>
                    <div className="text-2xl font-extrabold text-[#1B4D3E]">{fmtTRY(details.sale_price)}</div>
                  </div>
                  <Badge className={details.status === "satildi" ? "bg-rose-600 text-white" : "bg-emerald-600 text-white"}>{details.status.toUpperCase()}</Badge>
                </div>
                {details.buyer_name && (
                  <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
                    <div className="text-[11px] uppercase tracking-widest font-bold text-emerald-700">Alıcı</div>
                    <div className="font-bold text-slate-900">{details.buyer_name}</div>
                    <div className="text-xs text-slate-600">Satış: {fmtDate(details.sold_at)} · Tutar: {fmtTRY(details.sold_price)}</div>
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
